import Chart from 'chart.js/auto';
import { fmtN, fmtR, monthLabel } from './report-render.js';

/**
 * Reports: the graphs in the annexures (docs/reports.md).
 *
 * One spec per graph, drawn twice: on a canvas in the page, and offscreen
 * into a PNG for the print preview and the .docx. One series per graph gets
 * one colour; the only two-series graph (started vs left) uses the validated
 * blue/orange pair with a legend, and every bar carries its value.
 */

const INK = '#0F172A';
const MUTED = '#64748B';
const GRID = 'rgba(15,23,42,0.06)';
const SERIES = '#2563EB';            // one series → one colour
const SERIES_2 = '#EA6C0A';          // second series (passes CVD with SERIES; values labelled)
/** R3.7m / R850k — short enough to sit on a bar. */
const randShort = (v) => (Math.abs(v) >= 1e6 ? `R${(v / 1e6).toFixed(1)}m` : Math.abs(v) >= 1e3 ? `R${Math.round(v / 1e3)}k` : `R${v}`);
const tickN = (v) => fmtN(v);

/** Writes each bar's value at its end, so the graph reads without hovering (and in print). */
const valueLabels = {
  id: 'rpValues',
  afterDatasetsDraw(chart, _args, opts) {
    if (opts?.off) return;
    const { ctx } = chart;
    const horizontal = chart.options.indexAxis === 'y';
    ctx.save();
    ctx.font = `600 ${opts?.size || 11}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = INK;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.type !== 'bar' || meta.hidden) return;
      meta.data.forEach((bar, i) => {
        const v = ds.data[i];
        if (v == null || v === 0) return;
        const label = opts?.format ? opts.format(v) : fmtN(v);
        if (horizontal) { ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(label, bar.x + 6, bar.y); }
        else { ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(label, bar.x, bar.y - 4); }
      });
    });
    ctx.restore();
  },
};

function base(extra = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { top: 18, right: 48 } },
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: '#0D1B35', padding: 10, cornerRadius: 8, titleFont: { family: 'Inter', weight: '700' }, bodyFont: { family: 'Inter' } },
      ...extra.plugins,
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: MUTED, font: { family: 'Inter', size: 11 } }, border: { display: false } },
      y: { grid: { color: GRID }, ticks: { color: MUTED, font: { family: 'Inter', size: 11 }, callback: extra.yTick || tickN }, border: { display: false }, beginAtZero: true },
      ...extra.scales,
    },
    ...(extra.indexAxis ? { indexAxis: extra.indexAxis } : {}),
  };
}

const bar = (labels, data, { horizontal = false, format, tick } = {}) => ({
  type: 'bar',
  data: { labels, datasets: [{ data, backgroundColor: SERIES, borderRadius: 4, borderSkipped: 'start', maxBarThickness: horizontal ? 22 : 40 }] },
  options: base({
    indexAxis: horizontal ? 'y' : undefined,
    yTick: tick,
    plugins: { rpValues: { format }, tooltip: { callbacks: { label: (c) => (tick === randShort ? fmtR(c.raw) : format ? format(c.raw) : fmtN(c.raw)) } } },
    scales: horizontal
      ? { x: { grid: { color: GRID }, ticks: { color: MUTED, font: { family: 'Inter', size: 11 }, callback: tickN }, border: { display: false }, beginAtZero: true },
          y: { grid: { display: false }, border: { display: false }, ticks: { color: INK, font: { family: 'Inter', size: 11 },
            // a long name on a phone-width graph: shorten rather than clip
            callback(v) { const l = String(this.getLabelForValue(v)); return this.chart.width < 480 && l.length > 20 ? `${l.slice(0, 19)}…` : l; } } } }
      : {},
  }),
  plugins: [valueLabels],
});

const line = (labels, data, { min, max, format } = {}) => ({
  type: 'line',
  data: { labels, datasets: [{ data, borderColor: SERIES, backgroundColor: 'rgba(37,99,235,0.08)', fill: true, borderWidth: 2, pointRadius: 4, pointBackgroundColor: SERIES, pointBorderColor: '#fff', pointBorderWidth: 2, tension: 0.25 }] },
  options: base({
    plugins: { tooltip: { mode: 'index', intersect: false, callbacks: { label: (c) => (format ? format(c.raw) : fmtN(c.raw)) } } },
    scales: { y: { grid: { color: GRID }, ticks: { color: MUTED, font: { family: 'Inter', size: 11 }, callback: (v) => (format ? format(v) : fmtN(v)) }, border: { display: false }, min, max, beginAtZero: min == null } },
  }),
});

/** The graphs for one annexure block, from its stored snapshot. */
export function chartSpecs(block, d) {
  if (!d) return [];
  if (block === 'demographics') {
    const h = (rows) => Math.max(160, 44 * rows.length + 40);
    return [
      { id: 'gender', title: 'Beneficiaries by gender', height: h(d.gender || []), config: bar((d.gender || []).map((x) => x.label), (d.gender || []).map((x) => x.n), { horizontal: true }) },
      { id: 'age', title: 'Beneficiaries by age band (age as at the report date)', height: 240, config: bar((d.age_bands || []).map((x) => x.label), (d.age_bands || []).map((x) => x.n)) },
      { id: 'race', title: 'Beneficiaries by population group', height: h(d.race || []), config: bar((d.race || []).map((x) => x.label), (d.race || []).map((x) => x.n), { horizontal: true }) },
      { id: 'municipality', title: 'Beneficiaries by municipality', wide: true, height: h(d.by_municipality || []), config: bar((d.by_municipality || []).map((x) => x.label), (d.by_municipality || []).map((x) => x.n), { horizontal: true }) },
    ];
  }
  if (block === 'progress') {
    const m = d.months || [];
    const labels = m.map((x) => monthLabel(x.month));
    const pctMin = Math.max(0, Math.floor((Math.min(...m.map((x) => x.attendance_pct ?? 100)) - 5) / 10) * 10);
    return [
      { id: 'in_training', title: 'Learners in training each month', height: 240, config: line(labels, m.map((x) => x.in_training)) },
      { id: 'attendance', title: 'Attendance rate each month (present ÷ marked)', height: 240, config: line(labels, m.map((x) => x.attendance_pct), { min: pctMin, max: 100, format: (v) => `${v}%` }) },
      { id: 'flow', title: 'Learners who started and left each month', height: 240, config: {
        type: 'bar',
        data: { labels, datasets: [
          { label: 'Started', data: m.map((x) => x.starters), backgroundColor: SERIES, borderRadius: 4, borderSkipped: 'start', maxBarThickness: 26 },
          { label: 'Left', data: m.map((x) => x.left), backgroundColor: SERIES_2, borderRadius: 4, borderSkipped: 'start', maxBarThickness: 26 },
        ] },
        options: base({ plugins: { legend: { display: true, position: 'top', align: 'start', labels: { color: INK, boxWidth: 12, boxHeight: 12, font: { family: 'Inter', size: 11 } } }, rpValues: { size: 10 } } }),
        plugins: [valueLabels],
      } },
      { id: 'stipends', title: 'Stipends calculated each month (gross)', height: 240, config: bar(labels, m.map((x) => Number(x.stipend_gross)), { format: randShort, tick: randShort }) },
    ];
  }
  return [];
}

/** Draws specs into a container's canvases. Returns the charts so the caller can destroy them. */
export function mountCharts(container, specs) {
  return specs.map((s) => {
    const canvas = container.querySelector(`canvas[data-chart="${s.id}"]`);
    return canvas ? new Chart(canvas, s.config) : null;
  }).filter(Boolean);
}

/** One graph as a PNG (white background) for print and the .docx. */
export async function chartPng(spec, width = 1200) {
  const height = Math.round((spec.height || 240) * (width / 640));
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  canvas.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;height:${height}px`;
  document.body.appendChild(canvas);
  const scale = width / 640;
  const cfg = structuredCloneConfig(spec.config, scale);
  const whiteBackground = {
    id: 'rpWhite',
    beforeDraw(c) { const x = c.ctx; x.save(); x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height); x.restore(); },
  };
  const chart = new Chart(canvas, { ...cfg, options: { ...cfg.options, responsive: false, devicePixelRatio: 1 }, plugins: [whiteBackground, ...(cfg.plugins || [])] });
  chart.draw();   // synchronous: animation is off, so the PNG is complete
  const url = canvas.toDataURL('image/png');
  chart.destroy();
  canvas.remove();
  return { url, width, height };
}

/** Scale fonts and padding for a large offscreen render (Chart.js sizes text in px). */
function structuredCloneConfig(cfg, scale) {
  const font = (f) => ({ ...(f || {}), size: Math.round(((f && f.size) || 11) * scale) });
  const o = cfg.options;
  const scales = Object.fromEntries(Object.entries(o.scales || {}).map(([k, v]) => [k, { ...v, ticks: { ...v.ticks, font: font(v.ticks?.font) } }]));
  return {
    ...cfg,
    options: {
      ...o,
      layout: { padding: { top: 18 * scale, right: 48 * scale } },
      scales,
      plugins: {
        ...o.plugins,
        tooltip: { enabled: false },
        legend: o.plugins?.legend?.display ? { ...o.plugins.legend, labels: { ...o.plugins.legend.labels, boxWidth: 12 * scale, boxHeight: 12 * scale, font: font(o.plugins.legend.labels?.font) } } : o.plugins?.legend,
        rpValues: { ...(o.plugins?.rpValues || {}), size: Math.round((o.plugins?.rpValues?.size || 11) * scale) },
      },
    },
    data: { ...cfg.data, datasets: cfg.data.datasets.map((ds) => ({ ...ds,
      ...(ds.maxBarThickness ? { maxBarThickness: ds.maxBarThickness * scale } : {}),
      ...(ds.borderWidth ? { borderWidth: ds.borderWidth * scale } : {}),
      ...(ds.pointRadius ? { pointRadius: ds.pointRadius * scale, pointBorderWidth: (ds.pointBorderWidth || 0) * scale } : {}),
      ...(ds.borderRadius ? { borderRadius: ds.borderRadius * scale } : {}) })) },
  };
}
